import { Cookie as CookieNames } from '@ps/web-core-enums';
import { canUseDOM, integrationTesting } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { StaticRouterContext } from 'react-router';
import { Redirect, useHistory, useLocation, withRouter } from 'react-router';
import Cookies from 'universal-cookie';

import { CMSReferences } from '@swf/cms';
import StarsModal from '@swf/components/StarsModal/StarsModal';
import useDebouncedSelector from '@swf/core/hooks/useDebouncedSelector';
import { AnalyticsContext } from '@swf/core/providers/AnalyticsProvider';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { decodeQuery } from '@swf/core/utils';
import layouts from '@swf/layouts/index';

import { CacheControl } from '../ResponseHeaders/ResponseHeaders';

import NotFound from './components/NotFound/NotFound';

interface Props {
  staticContext?: StaticRouterContext;
}

const cookies = new Cookies();

const StarsPage: React.FC<Props> = ({ staticContext = {} }) => {
  const redirectPath = useSelector(select.location.getRedirectPath);
  const history = useHistory();
  const page = useSelector(select.cms.getPage);
  const query = useSelector(select.location.getQuery);
  const urlPathname = useSelector(select.cms.getUrlPathname);
  const redirect = useSelector(select.cms.getRedirect);
  const target = useSelector(select.cms.getRedirectTarget);
  const geoRedirect = useSelector(select.cms.getGeoRedirect);
  const geoTarget = useSelector(select.cms.getGeoRedirectTarget);
  const brand = useSelector(select.cms.getBrandId);
  const dispatch = useDispatch<Dispatch>();
  const strippedPathname = useSelector((rootState: RootState) =>
    // @ts-ignore fix core types stripBaseURL expects 1 parameter
    select.location.stripBaseURL(rootState, undefined)
  );
  const analyticsPath = useSelector(select.location.getAnalyticsPath);
  const prevAnalyticsPath = useRef(analyticsPath);
  const { pageView } = useTrackers();
  const { pathname, hash } = useLocation();
  const { initialized: trackingInitialized } = useContext(AnalyticsContext);
  const { cms } = useDispatch<Dispatch>();
  const Layout = layouts[`${page?.layout.layout_type ?? 'StarsLayout'}`];
  // TODO: replace with "@rematch/loading" for cms/fetchPage.
  // useSelector((rootState: RootState) => rootState.loading.models.cms)
  // Remove `pageLoaded` from cms model
  const loading = useDebouncedSelector(select.cms.getPageLoading, integrationTesting ? 1000 : 0);

  useEffect(() => {
    if (!loading && urlPathname !== strippedPathname) {
      cms.fetchPage({ href: window.location.href });
    }
  }, [cms, loading, urlPathname, strippedPathname]);

  useEffect(() => {
    dispatch.localSettings.set({ registrationBrand: brand, redirectTo: urlPathname });
  }, [brand, dispatch.localSettings, urlPathname]);

  useEffect(() => {
    if (redirectPath) {
      dispatch.location.update(redirectPath);
      history.replace(redirectPath);
    }
  }, [dispatch.location, history, redirectPath]);

  useEffect(() => {
    const { modal, mitid, ...rest } = query;
    if (modal) dispatch.modal.enqueue({ path: modal, locationState: decodeQuery(mitid) || rest });
  }, [dispatch.modal, query]);

  useEffect(() => {
    if (geoRedirect?.soft_redirect && !cookies.get(CookieNames.SOFT_REDIRECT_CANCELLED)) {
      dispatch.modal.enqueue({ path: '/notifications/redirected' });
    }
  }, [dispatch.modal, geoRedirect]);

  useEffect(() => {
    if (trackingInitialized && hash && `${pathname}${hash}` !== prevAnalyticsPath.current) {
      pageView(`${pathname}${hash}`);
      prevAnalyticsPath.current = `${pathname}${hash}`;
    }
  }, [hash, pageView, pathname, trackingInitialized]);

  if (target && redirect?.status_code) {
    staticContext.statusCode = parseInt(redirect.status_code);
    return <Redirect to={target} />;
  }

  if (geoTarget && !geoRedirect?.soft_redirect) {
    const location = `${geoTarget}?modal=/notifications/redirected`;
    canUseDOM ? (window.location.href = location) : (staticContext.url = location);
    return null;
  }

  if (!page) {
    staticContext.statusCode = 404;
  }

  return page ? (
    <>
      <CMSReferences references={page.content?.[0]?.seo} />
      <Layout {...page} />
      <StarsModal />
    </>
  ) : (
    <>
      <CacheControl>no-store</CacheControl>
      <NotFound />
    </>
  );
};

export default withRouter(StarsPage);
